/**
 * Centrifuge 메시지 처리를 위한 순수 함수 모음
 *
 * 이 파일의 모든 함수는 순수 함수입니다:
 * - 동일한 입력에 대해 항상 동일한 출력 반환
 * - Side effect 없음
 * - 외부 상태 변경 없음
 *
 * 사용처:
 * - PlayerContainer (Mobile)
 * - PlayContainer (PC)
 * - AspPlayClient
 * - CastSettingContainer
 */

import type { PlayInfo } from '@module/apis/client-side/live/type';
import type {
  PersonalManagerOnOffMessage,
  PersonalOverLapLoginMessage,
} from '@module/types/centrifuge/personalMessageTypes';
import type {
  BlindOnMessage,
  CastGuestImgBlindOnMessage,
  CastPauseMessage,
  ServiceBlindOnMessage,
} from '@module/types/centrifuge/public-message/blindMessageTypes';
import type { ChatBlockCountryMessage } from '@module/types/centrifuge/public-message/ChatBlockCountryMessageTypes';
import type { ModifyRoomMessage } from '@module/types/centrifuge/public-message/etcMessageTypes';
import type { ExcelBroadcastMessage } from '@module/types/centrifuge/public-message/excelMessageTypes';
import type { FreezeOnMessage } from '@module/types/centrifuge/public-message/freezeMessageTypes';
import type { MissionMessage } from '@module/types/centrifuge/public-message/missionMessageTypes';
import type { VoteMessage } from '@module/types/centrifuge/public-message/voteMessageTypes';

/**
 * FreezeOn 메시지 처리: 채팅 얼리기 활성화
 * @param payload FreezeOn 메시지 payload
 * @param playInfo 현재 PlayInfo
 * @returns 업데이트된 PlayInfo
 */
export const createFreezeOnUpdate = (payload: FreezeOnMessage, playInfo: PlayInfo): PlayInfo => {
  const { freezeFanExcpt, freezeManagerExcpt, name } = payload.data.message;

  return {
    ...playInfo,
    roomFreezeInfo: {
      fanExcpt: freezeFanExcpt || '',
      managerExcpt: freezeManagerExcpt || '',
      userType: '',
      ...playInfo.roomFreezeInfo,
      enable: true,
      name,
    },
  };
};

/**
 * FreezeOff 메시지 처리: 채팅 얼리기 비활성화
 * @param playInfo 현재 PlayInfo
 * @returns 업데이트된 PlayInfo
 */
export const createFreezeOffUpdate = (playInfo: PlayInfo): PlayInfo => {
  return {
    ...playInfo,
    roomFreezeInfo: {
      fanExcpt: '',
      managerExcpt: '',
      name: '',
      userType: '',
      ...playInfo.roomFreezeInfo,
      enable: false,
    },
  };
};

/**
 * Mission 메시지 처리: 미션 상태 업데이트
 * @param payload Mission 메시지 payload
 * @param playInfo 현재 PlayInfo
 * @returns 업데이트된 PlayInfo (조건에 맞지 않으면 원본 반환)
 */
export const createMissionUpdate = (payload: MissionMessage, playInfo: PlayInfo): PlayInfo => {
  if (!playInfo.mission) return playInfo;

  const messageType = payload.data.message.type;
  if (messageType !== 'on' && messageType !== 'off') return playInfo;

  return {
    ...playInfo,
    mission: {
      stat: messageType,
      coin: playInfo.mission.coin,
    },
  };
};

/**
 * Vote 메시지 처리: 투표 상태 업데이트
 * @param payload Vote 메시지 payload
 * @param playInfo 현재 PlayInfo
 * @returns 업데이트된 PlayInfo
 */
export const createVoteUpdate = (payload: VoteMessage, playInfo: PlayInfo): PlayInfo => {
  if (payload.data.type === 'VoteStart') {
    return {
      ...playInfo,
      voteInfo: { stat: 'success', idx: 0 },
    };
  }

  if (payload.data.type === 'VoteHide') {
    const newPlayInfo = { ...playInfo };
    delete newPlayInfo.voteInfo;
    return newPlayInfo;
  }

  return playInfo;
};

/**
 * ModifyRoom 메시지 처리: 방송 정보 업데이트
 * @param payload ModifyRoom 메시지 payload
 * @param playInfo 현재 PlayInfo
 * @returns 업데이트된 PlayInfo (조건에 맞지 않으면 원본 반환)
 */
export const createModifyRoomUpdate = (payload: ModifyRoomMessage, playInfo: PlayInfo): PlayInfo => {
  if (!playInfo.media) return playInfo;

  return {
    ...playInfo,
    media: {
      ...playInfo.media,
      title: payload.data.message.title,
      isPw: payload.data.message.isPw,
      isAdult: payload.data.message.isAdult,
    },
  };
};

/**
 * ModifyRoom 메시지의 부가 조건 체크
 * @param payload ModifyRoom 메시지 payload
 * @param playInfo 현재 PlayInfo
 * @param isLogin 로그인 여부
 * @param isAdult 성인 인증 여부
 * @param siteMode 사이트 모드
 * @returns 방 나가기 메시지 또는 null
 */
export const checkModifyRoomConstraints = (
  payload: ModifyRoomMessage,
  playInfo: PlayInfo,
  isLogin: boolean,
  isAdult: boolean,
  siteMode?: { mode: string } | null,
): { shouldExit: boolean; message?: string; shouldRefetch?: boolean } => {
  const message = payload.data.message;

  // 성인 방송으로 변경 + adultOut 플래그
  if (message.isAdult && playInfo.adultOut) {
    return {
      shouldExit: true,
      message: '해당 방송 시청이 불가능합니다. 고객센터로 문의해 주세요.',
    };
  }

  // 비밀번호 방으로 변경 + 비로그인 상태
  if (message.isPw && !playInfo.media.isPw && !isLogin) {
    return {
      shouldExit: true,
      message: '방송설정이 변경되었습니다.',
    };
  }

  // 성인 방송으로 변경 + 성인 미인증
  if (message.isAdult && !playInfo.media.isAdult && !isAdult) {
    if (siteMode && ['w', 'c'].includes(siteMode.mode)) {
      return {
        shouldExit: true,
        message: '방송설정이 변경되었습니다.',
      };
    }
    return {
      shouldExit: false,
      shouldRefetch: true,
    };
  }

  return { shouldExit: false };
};

/**
 * ExcelBroadcast 메시지 처리: 엑셀 방송 상태 업데이트
 * @param payload ExcelBroadcast 메시지 payload
 * @param playInfo 현재 PlayInfo
 * @returns 업데이트된 PlayInfo
 */
export const createExcelBroadcastUpdate = (payload: ExcelBroadcastMessage, playInfo: PlayInfo): PlayInfo => {
  return {
    ...playInfo,
    excelBroadcast: payload.data.message,
  };
};

/**
 * BlindOn 메시지 처리: 블라인드 켜기
 * @param payload BlindOn, CastGuestImgBlindOn, CastPause, MemberKickOut 메시지 payload
 * @param playInfo 현재 PlayInfo
 * @returns 업데이트된 PlayInfo
 */
export const createBlindOnUpdate = (
  payload:
    | BlindOnMessage
    | CastGuestImgBlindOnMessage
    | CastPauseMessage
    | PersonalOverLapLoginMessage
    | ServiceBlindOnMessage,
  playInfo: PlayInfo,
): PlayInfo => {
  return {
    ...playInfo,
    roomBlindInfo: { blindType: payload.data.type, enable: true, name: '' },
  };
};

/**
 * BlindOff 메시지 처리: 블라인드 해제
 * @param playInfo 현재 PlayInfo
 * @returns 업데이트된 PlayInfo
 */
export const createBlindOffUpdate = (playInfo: PlayInfo): PlayInfo => {
  return {
    ...playInfo,
    roomBlindInfo: { blindType: '', enable: false, name: '' },
  };
};

/**
 * PersonalManagerOnOff 메시지 처리: 매니저 임명/해임
 * @param payload PersonalManagerOnOff 메시지 payload
 * @param playInfo 현재 PlayInfo
 * @returns 업데이트된 PlayInfo
 */
export const createManagerUpdate = (payload: PersonalManagerOnOffMessage, playInfo: PlayInfo): PlayInfo => {
  return {
    ...playInfo,
    fan: {
      ...playInfo.fan,
      isManager: payload.data.message.func === 'ManagerOn',
    },
  };
};

/**
 * ChatBlockCountry 메시지 처리: 국가별 채팅 차단 설정
 * @param payload ChatBlockCountry 메시지 payload
 * @param playInfo 현재 PlayInfo
 * @returns 업데이트된 PlayInfo
 */
export const createChatBlockCountryUpdate = (payload: ChatBlockCountryMessage, playInfo: PlayInfo): PlayInfo => {
  const { message } = payload.data;

  return {
    ...playInfo,
    blockedLocales: message.blockedLocales,
  };
};
